const React = require('react');
const T = require('prop-types');

const {
    useState,
    forwardRef
} = React;

const { default: List } = require('@mui/material/List');
const { default: ListItemIcon } = require('@mui/material/ListItemIcon');
const { default: ListItemText } = require('@mui/material/ListItemText');
const { default: ExpandLess } = require('@mui/icons-material/ExpandLess');
const { default: ExpandMore } = require('@mui/icons-material/ExpandMore');
const { default: Collapse } = require('@mui/material/Collapse');
const { default: Badge } = require('@mui/material/Badge');
const { default: Checkbox } = require('@mui/material/Checkbox');
const { default: FormControlLabel } = require('@mui/material/FormControlLabel');

const AppMenuItemComponent = require('./AppMenuItemComponent');
const { default: Styled } = require('styled-components');
const { theme } = require('styles/theme');

const NearpeerTheme = theme();

// React runtime PropTypes
const AppMenuItemPropTypes = {
    name: T.string.isRequired,
    link: T.string,
    Icon: T.oneOfType([
        T.elementType,
        T.element,
        T.string
    ]),
    items: T.array,
    inset: T.bool,
    onClick: T.func,
    closeMainMenu: T.func,
    push: T.func,
    badgeNumber: T.number,
    $leftIndent: T.number,
    isCheckbox: T.bool,
    isChecked: T.bool,
    onCheck: T.func,
    description: T.string
};

const internals = {};

internals.renderMenuIcon = (Icon) => {

    if (!Icon) {
        return null;
    }

    if (typeof Icon === 'string') {
        return (
            <img
                src={Icon}
                alt=''
                aria-hidden='true'
                className='menuItemIcon'
            />
        );
    }

    if (React.isValidElement(Icon)) {
        return Icon;
    }

    return <Icon />;
};

const AppMenuItem = forwardRef((props, ref) => {

    const {
        name,
        link,
        onClick,
        closeMainMenu,
        Icon,
        inset = false,
        items = [],
        push,
        badgeNumber,
        $leftIndent = 0,
        isCheckbox,
        isChecked,
        onCheck,
        description,
        ...rest
    } = props;

    const hasBadge = badgeNumber && (badgeNumber === 'NEW' || badgeNumber > 0);

    const {
        AppMenuItemComponentStyled,
        StyledList,
        StyledBadge,
        TextBadgeLockup,
        StyledCheckbox
    } = internals;

    const isExpandable = items && items.length > 0;

    let isHighlighted = link === window.location.pathname;

    if (items.length > 0) {
        isHighlighted = !!items.find(({ link: _link }) => _link === window.location.pathname);
    }

    const [isOpen, setIsOpen] = useState(isHighlighted);

    if (!isExpandable) {
        if (isCheckbox) {
            return (
                <AppMenuItemComponentStyled
                    component='li'
                    link={link}
                    isHighlighted={isHighlighted}
                    ref={ref}
                    $leftIndent={$leftIndent}
                    {...rest}
                >
                    <TextBadgeLockup>
                        <FormControlLabel
                            labelPlacement='end'
                            label={
                                <ListItemText
                                    primary={name}
                                    secondary={description || null}
                                    inset={inset}
                                />
                            }
                            control={
                                <StyledCheckbox
                                    checked={isChecked}
                                    onChange={(e) => onCheck(e.target.checked)}
                                    size='small'
                                    name='agree-to-terms-checkbox'
                                    theme={NearpeerTheme}
                                    inputProps={{ 'data-focus-outline': 'radius:40', role: 'checkbox' }}
                                />
                            }
                        />
                    </TextBadgeLockup>
                    {/* Display the expand menu if the item has children */}
                </AppMenuItemComponentStyled>
            );
        }

        return (
            <AppMenuItemComponentStyled
                component='li'
                link={link}
                onClick={onClick}
                isHighlighted={isHighlighted}
                ref={ref}
                $leftIndent={$leftIndent}
                {...rest}
            >
                {/* Display an icon if any */}
                {Icon && (
                    <ListItemIcon>
                        {!hasBadge && internals.renderMenuIcon(Icon)}
                    </ListItemIcon>
                )}
                <TextBadgeLockup>
                    <ListItemText
                        primary={name}
                        secondary={description || null}
                        inset={inset}
                    />
                    {!!hasBadge && <StyledBadge badgeContent={badgeNumber} />}
                </TextBadgeLockup>
                {/* Display the expand menu if the item has children */}
            </AppMenuItemComponentStyled>
        );
    }

    const collapseId = `submenu-${name.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`;

    const toggleCollapse = () => {

        setIsOpen((prevOpen) => !prevOpen);
    };

    return (
        <>
            <AppMenuItemComponentStyled
                component='li'
                ref={ref}
                onClick={toggleCollapse}
                isHighlighted={isHighlighted}
                aria-expanded={isOpen}
                aria-controls={collapseId}
            >
                {Icon && (
                    <ListItemIcon>
                        {internals.renderMenuIcon(Icon)}
                    </ListItemIcon>
                )}
                <ListItemText
                    primary={name}
                    secondary={description || null}
                    inset={inset}
                />
                {isOpen ? <ExpandLess aria-hidden='true' /> : <ExpandMore aria-hidden='true' />}
            </AppMenuItemComponentStyled>
            <Collapse in={isOpen} timeout='auto' unmountOnExit>
                <StyledList component='div' id={collapseId} disablePadding>
                    {items.map((item) => (

                        // Recursively use this component
                        <AppMenuItem
                            closeMainMenu={closeMainMenu}
                            push={push}
                            key={item.name}
                            $leftIndent={$leftIndent + 1}
                            {...item}
                            onClick={() => {

                                if (item.onClick) {
                                    item.onClick();
                                }

                                if (item.link) {
                                    push(item.link);
                                }

                                if (!item.items) {
                                    closeMainMenu();
                                }
                            }}
                        />
                    ))}
                </StyledList>
            </Collapse>
        </>
    );
});

AppMenuItem.displayName = 'AppMenuItem';
AppMenuItem.propTypes = AppMenuItemPropTypes;

internals.StyledList = Styled(List)`
    width: 100%;

    // Top shadow
    &::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 4px; // Adjust the height as desired
        background: linear-gradient(to bottom, rgba(0, 0, 0, 0.2), transparent);
    }

    // Bottom shadow
    &::after {
        content: "";
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 4px; // Adjust the height as desired
        background: linear-gradient(to top, rgba(0, 0, 0, 0.2), transparent);
    }
`;

internals.StyledBadge = Styled(Badge)`
    position: absolute;
    left: 27px;

    .MuiBadge-badge {
        background-color: ${NearpeerTheme.palette.secondary.main};
        color: white;
    }
`;

internals.TextBadgeLockup = Styled.div`
    flex-grow: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

internals.AppMenuItemComponentStyled = Styled(AppMenuItemComponent)`
    min-height: 48px;

    width: 100%;

    display: flex;

    &.Mui-disabled {
        opacity: 1;

        & .MuiListItemText-root {
            & .MuiTypography-root {
                color: rgb(70, 17, 169);
            }
        }
    }

    &:hover {
        background: ${({ theme }) => theme.palette.menu.hover};
    }

    &:focus-visible {
        outline: 2px solid ${NearpeerTheme.palette.primary.main} !important;
        outline-offset: -2px;
        background: ${({ theme }) => theme.palette.menu.hover};
    }

    .MuiListItemIcon-root {
        min-width: 36px;
        display: flex;
        align-items: center;

        svg {
            fill: black;
        }

        img.menuItemIcon {
            display: block;
            width: 30px;
            height: 30px;
            min-width: 30px;
            object-fit: contain;
            flex-shrink: 0;
            margin-left: -4px;
        }
    }

    .MuiListItemText-root {
        margin: 8px 0;
    }

    .MuiListItemText-secondary {
        line-height: 1.3;
        margin-top: 2px;
    }

    ${({ theme, isHighlighted, $leftIndent }) => {

        let styles = `
            padding-left: ${theme.spacing($leftIndent + 2)};

            @media (max-width: ${theme.breakpoints.values.mobileL}px) {
                & .MuiListItemIcon-root,
                & .menuItemIcon {
                    font-size: 0.8rem;
                }

                & .MuiListItemText-root {
                    & .MuiTypography-root {
                        font-size: 0.8rem;
                    }
                }
            }
        `;

        if (isHighlighted) {
            styles = `${styles}

            background: ${NearpeerTheme.palette.primary.main} !important;
            color: #fff !important;

            & .MuiListItemIcon-root,
            & .menuItemIcon {
                color: #fff;
            }

            & .MuiListItemText-root {
                & .MuiTypography-root {
                    color: #fff;
                }
            }

            .MuiListItemIcon-root {
                svg {
                    fill: white;
                }
            }
            `;
        }

        return styles;
    }}
`;

module.exports = AppMenuItem;

internals.StyledCheckbox = Styled(Checkbox)`
    &&& {
        color: ${({ theme, mode }) => mode === 'dark' ? theme.palette.common.white : theme.palette.primary.main};
        fill: ${({ theme, mode }) => mode === 'dark' ? theme.palette.common.white : theme.palette.primary.main};
    }
`;
